import { useCallback, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { useLocalStorage, useUserActivityCreatedListener, useNotificationReceivedListener } from "app/hooks";
import notificationsTypes from "constants/notificationsTypes";
import { useProfile } from "app/context";
import { isAdmin } from "app/permissions";
import { useUserActivities } from "./UserActivitiesContext";

const isNewer = lastCreatedAt => activity => (
  new Date(activity.createdAt) > new Date(lastCreatedAt)
);

// This logic does not have to change, it is being used in FeedUpdates.js
export const isForEveryone = activity => {
  if (activity.activityType === "post_created") {
    const { entityMap } = JSON.parse(activity.entity.body);
    return (Object.keys(entityMap).filter(key => entityMap[key].type === "MENTION").length === 0);
  }

  return true;
};

const useUnseen = feedRoute => {
  const { profile, fetchedProfile } = useProfile();
  const userActivities = useUserActivities();
  const history = useHistory();
  const [lastCreatedAt, setLastCreatedAt] = useLocalStorage("last-activity-at", null);
  const [unseen, setUnseen] = useState({
    unseenHome: false,
    allActivity: false,
    forYou: false,
    pendingStoryReviews: profile.pendingStoryReviews?.length
  });

  useNotificationReceivedListener(
    useCallback(
      notification => {
        switch (notification.notificationType) {
          case notificationsTypes.FOR_YOU:
            setUnseen({
              ...unseen,
              unseenHome: true,
              allActivity: false,
              forYou: true
            });
            break;
          case notificationsTypes.NOT_FOR_YOU:
            setUnseen({
              ...unseen,
              unseenHome: true,
              allActivity: true,
              forYou: false
            });
            break;
          default:
            break;
        }
      },
      [unseen]
    )
  );

  useUserActivityCreatedListener(
    useCallback(
      activity => {
        const isInTheFeed = () => (history.location.pathname === feedRoute);
        const isNewActivity = () => (!lastCreatedAt || isNewer(lastCreatedAt)(activity));

        if (activity.activityType === "story_created" && isAdmin(profile)) {
          const newEntity = activity.childUserActivities.length > 0
            ? activity.childUserActivities[0].entity
            : activity.entity;
          if (newEntity.creator.id !== profile.id) {
            const newId = newEntity.id;
            fetchedProfile({
              ...profile,
              pendingStoryReviews: [...profile.pendingStoryReviews, newId]
            });
          }
        }

        if (isInTheFeed()) {
          if (isForEveryone(activity)) {
            setUnseen({
              ...unseen, unseenHome: false, allActivity: false, forYou: false
            });
            return;
          }

          if (isNewActivity()) {
            setUnseen({
              ...unseen, unseenHome: false, allActivity: false, forYou: true
            });
          }
        }

        if (isNewActivity()) {
          if (isForEveryone(activity)) {
            setUnseen({
              ...unseen, unseenHome: true, allActivity: false, forYou: false
            });
          } else {
            setUnseen({
              ...unseen, unseenHome: false, allActivity: false, forYou: false
            });
          }
          setLastCreatedAt(activity.createdAt);
        }
      },
      [history.location.pathname, feedRoute, lastCreatedAt, setUnseen, setLastCreatedAt,
        fetchedProfile, profile, unseen]
    )
  );

  useEffect(
    () => {
      setUnseen({
        ...unseen,
        pendingStoryReviews: profile.pendingStoryReviews?.length
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile.pendingStoryReviews]
  );

  useEffect(
    () => {
      if (history.location.pathname === feedRoute) {
        setUnseen({
          ...unseen,
          unseenHome: false,
          allActivity: unseen.allActivity,
          forYou: false
        });
        setLastCreatedAt(new Date().toISOString());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );


  const { pathname } = history.location;
  const isItSomethingNew = (pathname !== feedRoute && !unseen.allActivity && lastCreatedAt
    && userActivities.some(isNewer(lastCreatedAt)));

  if (isItSomethingNew) {
    setUnseen({
      ...unseen,
      unseenHome: true,
      allActivity: true,
      forYou: false
    });
  }

  return unseen;
};

export default useUnseen;
